import { useRef, useState } from 'react';

import { calendarICS } from '../../shared/calendar-export';
import type { LocalRecord } from '../../shared/model';
import type { Change } from '../../shared/productivity';
import { exportWorkspace, imageManifest, importWorkspace } from '../../shared/workspace-export';

import { workspace } from '../lib/engine';
import { download } from '../lib/files';
import { ActionIconButton, Button, Input, Modal } from './ui';

export function WorkspaceTransfer({ records }: { records: LocalRecord[] }) {
  const file = useRef<HTMLInputElement>(null);

  const [error, setError] = useState(''),
    [status, setStatus] = useState(''),
    [incoming, setIncoming] = useState<Change[]>();

  async function exportAll() {
    try {
      setError('');
      const data = await exportWorkspace(records);

      download(
        `cairn-workspace-${new Date().toISOString().slice(0, 10)}.json`,
        JSON.stringify(data),
        'application/json',
      );

      setStatus(
        `Exported ${data.records.length} records and ${data.images.length} embedded images. Keep this file private.`,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function checkImages() {
    try {
      const images = await imageManifest(records),
        storage = await navigator.storage?.estimate?.();

      setStatus(
        `${images.length} embedded images (${(images.reduce((n, image) => n + image.bytes, 0) / 1048576).toFixed(1)} MB) are present in the locally saved notes. Browser storage: ${((storage?.usage ?? 0) / 1048576).toFixed(1)} MB used / ${((storage?.quota ?? 0) / 1048576).toFixed(0)} MB quota.`,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <section className="detail-card">
      <h2>Export, restore & offline images</h2>
      <p>
        A full workspace export retains typed tables, hidden rows, links, dates and embedded image
        bytes. It is an ordinary JSON file containing private workspace content. Export vault
        contents from the vault separately. The ICS companion includes saved appointments, task
        deadlines, and birthdays for the next ten calendar years; JSON retains the complete rules.
      </p>
      <div className="button-row wrap">
        <Button variant="secondary" onClick={() => void exportAll()}>
          Export full workspace
        </Button>
        <ActionIconButton
          action="restore"
          label="Restore workspace export"
          onClick={() => file.current?.click()}
        />
        <Button
          variant="secondary"
          onClick={() => download('cairn-calendar.ics', calendarICS(records), 'text/calendar')}
        >
          Export calendar ICS
        </Button>
        <Button variant="secondary" onClick={() => void checkImages()}>
          Verify offline images
        </Button>
      </div>
      <Input
        ref={file}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={async e => {
          const selected = e.currentTarget.files?.[0];

          e.currentTarget.value = '';

          if (!selected) {
            return;
          }

          setError('');

          try {
            if (selected.size > 100 * 1024 * 1024) {
              throw new Error('This browser import is limited to 100 MB.');
            }

            setIncoming(await importWorkspace(JSON.parse(await selected.text())));
          } catch (e) {
            setError((e as Error).message);
          }
        }}
      />
      {status && (
        <p className="notice" role="status">
          {status}
        </p>
      )}
      {error && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
      {incoming && (
        <Modal open onClose={() => setIncoming(undefined)} title="Review workspace restore">
          <p>{incoming.length} records passed validation, including embedded-image checksums.</p>
          <p>
            {incoming.filter(i => records.some(r => r.id === i.id)).length} existing records will be
            replaced with imported versions. Earlier synchronized versions remain in server history.
            Other existing records are retained. Permanently deleted records on this device are
            skipped.
          </p>
          <div className="button-row">
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await workspace.saveMany(
                    incoming.filter(i => !records.some(r => r.id === i.id && r.deleted)),
                  );

                  setIncoming(undefined);

                  setStatus(
                    'Imported records saved locally. Keep Cairn open until synchronization completes.',
                  );
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            >
              Restore these records
            </Button>
            <Button variant="secondary" onClick={() => setIncoming(undefined)}>
              Cancel
            </Button>
          </div>
        </Modal>
      )}
    </section>
  );
}
